import { inject, Injectable } from "@angular/core";
import { catchError, map, Observable, of, switchMap } from "rxjs";
import { userContextStore } from "../../../../shared/data/user-context.store";
import { piaCatalogStore } from "../../data/pia-catalog.store";
import { ConfigService } from "../config/config.service";
import { PiaContextService } from "../pia-api/pia-context.service";

export interface PiaLocale {
  countryID: string;
  languageID: string;
}

/**
 * Maps the country / language the user picked in the poff profile menu onto a
 * locale PIA can actually serve.
 *
 * The two catalogues are not identical: the profile offers the sales-org
 * countries the user is authorized for (ISO codes), while PIA carries its own
 * country list including the pseudo country `int` (international). Languages
 * are per PIA country as well. So both axes are validated against the PIA
 * catalogue and fall back to the configured defaults instead of leaving PIA in
 * an empty state.
 *
 * This service only reads the profile — it never writes back to
 * `userContextStore`, so reconciling PIA's product URL cannot cascade into
 * reloads of navigation, footer links or applications.
 */
@Injectable({ providedIn: "root" })
export class PiaLocaleService {
  private readonly contextService = inject(PiaContextService);
  private readonly config = inject(ConfigService);
  private readonly userContext = inject(userContextStore);
  private readonly catalog = inject(piaCatalogStore);

  /** Currently selected profile language, lower cased as used in PIA URLs. */
  get language(): string {
    return this.userContext.language().toLowerCase();
  }

  /**
   * Resolves the given profile country (defaults to the current selection)
   * against PIA's country list. Unknown countries — and a failing country
   * request — fall back to `environment.pia.countryDefault`.
   *
   * While the international catalogue is switched on in PIA, the profile
   * country is bypassed entirely: that switch is the user's explicit choice and
   * outranks their profile country.
   */
  resolveCountry(profileCountry?: string): Observable<string> {
    const fallback = this.config.countryDefault.toLowerCase();
    if (this.catalog.international()) {
      return of(fallback);
    }

    const country = (profileCountry ?? this.userContext.country())
      .trim()
      .toLowerCase();
    if (!country) {
      return of(fallback);
    }

    return this.contextService.getCountries().pipe(
      map((countries) =>
        countries.some((entry) => entry.key.toLowerCase() === country)
          ? country
          : fallback,
      ),
      catchError((error) => {
        console.error(
          "[PiaLocale] failed to load PIA countries, falling back to the default country",
          error,
        );
        return of(fallback);
      }),
    );
  }

  /**
   * Full PIA locale for the current profile selection: the resolved country
   * plus the profile language if PIA offers it for that country, otherwise the
   * best available one. Used when entering PIA without an explicit locale in
   * the URL — an active country switch inside PIA keeps using the language
   * mismatch dialog instead.
   */
  resolveLocale(): Observable<PiaLocale> {
    const preferredLanguageID = this.language;

    return this.resolveCountry().pipe(
      switchMap((countryID) =>
        this.contextService
          .getLanguageSelectionForCountry({ countryID, preferredLanguageID })
          .pipe(
            map((selection) => ({
              countryID,
              languageID: (selection.wasPreferredLanguageAvailable
                ? preferredLanguageID
                : (selection.selectedLanguageID ?? this.config.languageDefault)
              ).toLowerCase(),
            })),
            catchError((error) => {
              console.error(
                "[PiaLocale] failed to negotiate the PIA language, falling back to the default language",
                error,
              );
              return of({
                countryID,
                languageID: this.config.languageDefault.toLowerCase(),
              });
            }),
          ),
      ),
    );
  }
}
